import { Text, View, Pressable, ScrollView, Image as RNImage } from 'react-native';
import { memo, useCallback, useState, useEffect, useLayoutEffect, useMemo, useRef, createContext, useContext } from 'react';
import { Badge } from 'react-native-paper';
import Animated, { Easing, runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import Avatar from '@/components/People/Avatar';
import { Query } from '@/utils/react-query/Queries';
import { Image } from 'expo-image';
import { signOut } from '@/utils/signOut';
import useAuthStore from '@/data/store/useAuthStore';
import usePlatform from '@/utils/hooks/usePlatform';
import { showAlert } from '@/utils/showAlert';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import Toast from 'react-native-toast-message';
import ToggleInput from '@/components/General/inputs/ToggleInput';
import Callout from '@/components/General/Callout';
import * as Sentry from '@sentry/react-native';
import * as ImagePicker from 'expo-image-picker';
import { renderManipulatedImage } from '@/utils/FileManager';
import { File } from 'expo-file-system';
import { decode } from 'base64-arraybuffer';
import mime from 'mime';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import useNotificationBadges from '@/utils/hooks/useNotificationBadges';
import NotificationBadge from '@/components/General/NotificationBadge';
import { usePathname, useRouter, useSegments, useLocalSearchParams } from 'expo-router';
import { logOutOfOrganization, getCachedOrgID } from '@/data/organization';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import SearchModal from './SearchModal';
import DropdownMenu, { dropdownIcons } from './DropdownMenu';
import Unicon from './Unicon';
import FeatureIcon, { getFeatureColorKey } from './FeatureIcon';
import * as Skeletons from '@/utils/constants/skeletons';
import ReleaseNotesModal from '@/components/General/ReleaseNotesModal';
import useReleaseNotesStore from '@/data/store/useReleaseNotesStore';
import useTabConfigStore from '@/data/store/useTabConfigStore';
import useDrawerMenuStore from '@/data/store/useDrawerMenuStore';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import { getLatestReleaseNote } from '@/assets/release-notes';
import useRenderProbe from '@/utils/hooks/useRenderProbe';

const ICON_SIZE = 18;

const SIDEBAR_WIDTH = 260;
const COLLAPSED_SIDEBAR_WIDTH = 68;
const COLLAPSE_ANIM_MS = 220; // matches NavSection expand/collapse timing

// Shared by NavItem/NavSection so every text element fades on the same
// collapseProgress without threading props through ~20 call sites.
// collapsed = currently collapsing/collapsed (drive layout + section force-open)
// hideDetails = fully collapsed (unmount text so icons can center)
const DrawerCollapseContext = createContext({ collapsed: false, hideDetails: false, fadeStyle: null });

const NavItem = ({ testID, icon, feature, label, isSelected, onPress, badge, iconLib, iconStyle, destructive }) => {
    const { hideDetails, fadeStyle } = useContext(DrawerCollapseContext);
    const { theme: { colors } } = useUnistyles();
    const [hovered, setHovered] = useState(false);
    const featureIconActive = hovered || isSelected;
    const featureColor = feature ? colors[getFeatureColorKey(feature)] || colors.primary : null;
    return (
        <Pressable testID={testID} accessibilityRole="button" accessibilityLabel={label} accessibilityState={{ selected: isSelected }} onPress={onPress} onHoverIn={() => setHovered(true)} onHoverOut={() => setHovered(false)} style={styles.navItem(isSelected, hideDetails)}>
            {isSelected && <View style={styles.selectedIndicator} />}
            <View style={styles.navIconWrap(hideDetails)}>
                {feature ? <FeatureIcon feature={feature} testID={`${testID}-feature-icon`} neutral active={featureIconActive}>
                        {icon && <Unicon name={icon} size={ICON_SIZE} colorVariant={featureIconActive ? getFeatureColorKey(feature) : 'admin'} library={iconLib} iconStyle={iconStyle} />}
                    </FeatureIcon> :
                    <FeatureIcon feature={destructive ? 'error' : 'admin'} testID={`${testID}-feature-icon`}>
                        <Unicon name={icon} size={ICON_SIZE} colorVariant={destructive ? "error" : isSelected ? "primary" : "admin"} library={iconLib} iconStyle={iconStyle} />
                    </FeatureIcon>}
                {badge > 0 && <View style={styles.badge}><Badge size={16}>{badge}</Badge></View>}
            </View>
            {!hideDetails && (
                <Animated.View style={[styles.navLabelWrap, fadeStyle]}>
                    <Text style={[styles.navLabel(isSelected, destructive), feature && featureIconActive && { color: featureColor }]} numberOfLines={1}>{label}</Text>
                </Animated.View>
            )}
        </Pressable>
    );
};

const NavSection = ({ label, children, testID, sectionKey, defaultOpen = true }) => {
    const isOpen = useDrawerMenuStore((state) => state.openSections[sectionKey]);
    const setSectionOpen = useDrawerMenuStore((state) => state.setSectionOpen);
    const { collapsed, fadeStyle } = useContext(DrawerCollapseContext);
    const open = isOpen === undefined ? defaultOpen : isOpen;
    // When the sidebar is collapsed to an icon rail, force every section open
    // so its icons stay visible; the header row fades out via fadeStyle.
    const effectiveOpen = collapsed ? true : open;
    const openRef = useRef(effectiveOpen);
    const measuredRef = useRef(0);
    const [hasMeasured, setHasMeasured] = useState(false);
    // -1 = "auto" (natural height, no constraint); >=0 = fixed animated height.
    // Start at -1 so unmeasured closed sections can lay out off-screen and measure.
    const heightValue = useSharedValue(-1);
    const rotateValue = useSharedValue(open ? 0 : 180);

    // Drive animation from store state. Only animate once we've measured so we
    // know the target height; before that, leave height auto (-1).
    useEffect(() => {
        openRef.current = effectiveOpen;
        if (hasMeasured) {
            heightValue.value = withTiming(effectiveOpen ? measuredRef.current : 0, { duration: 220, easing: Easing.inOut(Easing.ease) });
        }
        rotateValue.value = withTiming(effectiveOpen ? 0 : 180, { duration: 220, easing: Easing.inOut(Easing.ease) });
    }, [effectiveOpen, hasMeasured]);

    const toggle = () => {
        const next = !openRef.current;
        setSectionOpen(sectionKey, next);
    };

    const animatedHeightStyle = useAnimatedStyle(() => {
        const h = heightValue.value;
        if (h < 0) {
            return { height: undefined, overflow: 'visible' };
        }
        return { height: h, overflow: 'hidden' };
    });

    const animatedChevronStyle = useAnimatedStyle(() => ({
        transform: [{ rotate: `${rotateValue.value}deg` }],
    }));

    return (
        <View>
            {/* Header stays mounted (faded) while collapsed so icon groups
                keep a visual separator; disabled so it can't be toggled. */}
            <Animated.View style={fadeStyle} pointerEvents={collapsed ? 'none' : 'auto'}>
                <Pressable testID={testID} onPress={toggle} style={styles.sectionHeader}>
                    <Text style={styles.sectionLabel}>{label}</Text>
                    <Animated.View style={animatedChevronStyle}>
                        <Unicon name="chevron-down-outline" size={14} colorVariant="textTertiary" />
                    </Animated.View>
                </Pressable>
            </Animated.View>
            <Animated.View
                style={[animatedHeightStyle, !hasMeasured && !effectiveOpen && { position: 'absolute', left: -10000, top: 0, opacity: 0 }]}
                pointerEvents={effectiveOpen ? 'auto' : 'none'}
            >
                <View
                    onLayout={(e) => {
                        const h = e.nativeEvent.layout.height;
                        if (h > 0) {
                            measuredRef.current = h;
                            if (!hasMeasured) {
                                // First measurement: lock auto → fixed at natural height (no jump).
                                heightValue.value = effectiveOpen ? h : 0;
                                setHasMeasured(true);
                            } else if (openRef.current) {
                                // Content grew/shrunk while open: re-target the animation
                                // to the new natural height. onLayout only fires when the
                                // inner view's own height changes (not during expand/collapse),
                                // so this won't snap mid-animation.
                                heightValue.value = withTiming(h, { duration: 220, easing: Easing.inOut(Easing.ease) });
                            }
                        }
                    }}
                >
                    {children}
                </View>
            </Animated.View>
        </View>
    );
};

const DrawerMenu = memo(function DrawerMenu({ onClose, asSlideout = false, isVisible = true }) {
    if (__DEV__) { (globalThis.__renderCounts ||= {}).drawerMenu = ((globalThis.__renderCounts.drawerMenu || 0) + 1); }
    const active = !asSlideout || isVisible;
    const pathname = usePathname();
    const segments = useSegments();
    const { typeID: currentTypeID } = useLocalSearchParams();
    const currentTab = segments[0] === '(tabs)' && segments[1] ? segments[1] : '(home)';
    // Identity stays live so retained content cannot survive an account/org switch.
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);
    // Fall back to the JWT org claim: getLoggedInUserInfo returns false on
    // transient failures (slow DB, queue timeouts), which leaves loggedInUser
    // null and would disable this query — freezing the drawer skeleton and
    // hiding the org switcher until the 15-minute staleTime expires. The
    // claim is populated during checkAuth before the drawer mounts.
    const drawerOrgID = loggedInUser?.organizationID || getCachedOrgID();
    const { data: organization, isPending: loadingOrganization } = Query.organization.byId({
        id: drawerOrgID,
        options: { enabled: active && !!drawerOrgID, subscribed: active },
    });
    const { data: givingMerchant } = Query.giving.merchant({
        organizationID: loggedInUser?.organizationID,
        options: {
            enabled: active && !!loggedInUser?.organizationID && (!isFree || entitlements?.enableGiving === true),
            subscribed: active,
            staleTime: 1000 * 60 * 5,
            refetchOnMount: true,
        },
    });
    const { data: registrations } = Query.registrations.all({
        options: { enabled: active && !!loggedInUser?.isAdmin, subscribed: active },
    });
    const { data: smallGroupTypes } = Query.smallGroupTypes.all({
        options: { enabled: active && !isFree, subscribed: active },
    });
    const customTabs = useTabConfigStore(state => state.customTabs);
    const lastSentryEventID = useAuthStore(state => state.lastSentryEventID);
    const hasUnread = useReleaseNotesStore(state => state.hasUnread);
    const markAsSeen = useReleaseNotesStore(state => state.markAsSeen);
    const { totalUnread: notificationCount, connectUnread } = useNotificationBadges();
    const menuState = useMemo(() => ({
        pathname, currentTab, currentTypeID, loggedInUser, entitlements, organization,
        loadingOrganization, givingMerchant, registrations, smallGroupTypes, customTabs,
        lastSentryEventID, hasUnread, markAsSeen, notificationCount, connectUnread,
    }), [pathname, currentTab, currentTypeID, loggedInUser, entitlements, organization,
        loadingOrganization, givingMerchant, registrations, smallGroupTypes, customTabs,
        lastSentryEventID, hasUnread, markAsSeen, notificationCount, connectUnread]);
    const identity = `${loggedInUser?.accountID || loggedInUser?.id || ''}:${loggedInUser?.organizationID || ''}`;
    useRenderProbe(active ? 'drawer-visible' : 'drawer-hidden', {
        active, onClose, pathname, currentTab, currentTypeID, loggedInUser, entitlements, organization,
        loadingOrganization, givingMerchant, registrations, smallGroupTypes, customTabs,
        lastSentryEventID, hasUnread, notificationCount, connectUnread,
    });

    return <DrawerMenuContent key={identity} onClose={onClose} asSlideout={asSlideout} active={active} menuState={menuState} />;
});

// Route/cache observers above may update while hidden; retain the menu's last
// rendered snapshot until it is shown again, without unmounting its native tree.
const DrawerMenuContent = memo(function DrawerMenuContent({ onClose, asSlideout, active, menuState }) {
    const {
        pathname, currentTab, currentTypeID, loggedInUser, entitlements, organization,
        loadingOrganization, givingMerchant, registrations, smallGroupTypes, customTabs,
        lastSentryEventID, hasUnread, markAsSeen, notificationCount, connectUnread,
    } = menuState;
    const isFree = isFreeOrg(entitlements);
    const [feedbackMessage, setFeedbackMessage] = useState("");
    const [feedbackType, setFeedbackType] = useState(null);
    const [feedbackAttachment, setFeedbackAttachment] = useState(null);
    const [searchSheetOpen, setSearchSheetOpen] = useState(false);
    const [feedbackSheetOpen, setFeedbackSheetOpen] = useState(false);
    const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
    const feedbackGeneration = useRef(0);
    const resetFeedback = useCallback(() => {
        feedbackGeneration.current += 1;
        setFeedbackSheetOpen(false);
        setFeedbackMessage("");
        setFeedbackType(null);
        setFeedbackAttachment(null);
    }, []);
    useLayoutEffect(() => {
        // Invalidate before promise continuations can commit into a hidden or
        // different feedback session; cleanup also covers identity-key unmounts.
        if (!active || !feedbackSheetOpen) feedbackGeneration.current += 1;
        return () => { feedbackGeneration.current += 1; };
    }, [active, feedbackSheetOpen]);

    const { theme: { colors, scheme }, rt } = useUnistyles();
    const router = useRouter();
    const { isWeb, isLandscape, isPortrait } = usePlatform();

    // ── Collapsible landscape sidebar ──
    // Only the permanent landscape sidebar can collapse (never the slideout).
    const collapsedPref = useDrawerMenuStore((state) => state.collapsed);
    const setCollapsed = useDrawerMenuStore((state) => state.setCollapsed);
    const collapsible = isLandscape && !asSlideout;
    const collapsed = collapsible && collapsedPref;
    const collapseProgress = useSharedValue(collapsed ? 1 : 0);
    // Text elements stay mounted (fading) during the width animation, then
    // unmount via hideDetails so icons can center in the narrow rail.
    const [detailsHidden, setDetailsHidden] = useState(collapsed);
    const collapseAnimatingRef = useRef(false);
    const hideDetails = collapsible && detailsHidden;

    const fadeStyle = useAnimatedStyle(() => ({ opacity: 1 - collapseProgress.value }));
    const sidebarWidthStyle = useAnimatedStyle(() => ({
        width: SIDEBAR_WIDTH - (SIDEBAR_WIDTH - COLLAPSED_SIDEBAR_WIDTH) * collapseProgress.value,
    }));

    const collapseCtx = useMemo(
        () => ({ collapsed, hideDetails, fadeStyle }),
        [collapsed, hideDetails, fadeStyle]
    );

    const toggleCollapsed = () => {
        const next = !collapsedPref;
        setCollapsed(next);
        collapseAnimatingRef.current = true;
        if (!next) setDetailsHidden(false); // expanding: remount text immediately so it fades in
        collapseProgress.value = withTiming(next ? 1 : 0, { duration: COLLAPSE_ANIM_MS, easing: Easing.inOut(Easing.ease) }, (finished) => {
            collapseAnimatingRef.current = false;
            if (finished) runOnJS(setDetailsHidden)(next);
        });
    };

    // Snap (no animation) when collapsed changes from outside the toggle —
    // e.g. AsyncStorage hydration on mount or rotating to/from portrait.
    useEffect(() => {
        if (!collapseAnimatingRef.current) {
            collapseProgress.value = collapsed ? 1 : 0;
            setDetailsHidden(collapsed);
        }
    }, [collapsed]);

    const selectedTabKeys = useMemo(() => Object.values(customTabs || {}), [customTabs]);

    styles.useVariants({ isLandscape });

    // Derived directly — a refetch keeps serving the previous cached count,
    // matching the old "update when fetch completes" behavior.
    const registrationCount = registrations?.length || 0;

    // Bible Studies module gate — enableBibleStudies is UI-only; access needs
    // a rostered teacher seat in the study type's role or a module permission.
    const bibleStudiesEnabled = organization?.enableBibleStudies === true;
    const bibleStudyType = (smallGroupTypes || []).find(t => t.purpose === 'BIBLE_STUDY' && t.roleID)
        || (smallGroupTypes || []).find(t => t.purpose === 'BIBLE_STUDY') || null;
    const isBibleStudyTeacher = !!bibleStudyType?.roleID && !!loggedInUser?.Roles?.some(r => r.id === bibleStudyType.roleID);
    const canViewBibleStudies = bibleStudiesEnabled && (
        loggedInUser?.isAdmin
        || loggedInUser?.can?.('Manage Bible Studies')
        || loggedInUser?.can?.('View Bible Studies')
        || isBibleStudyTeacher
    );

    // Closing the slideout used to unmount the menu (and its sheets). With the
    // tree retained, hide any presented sheet so it can't linger invisible.
    useEffect(() => {
        if (active) return;
        setSearchSheetOpen(false);
        setReleaseNotesOpen(false);
        resetFeedback();
    }, [active, resetFeedback]);

    const initReleaseNotes = useReleaseNotesStore(s => s.init);
    const checkUnread = useReleaseNotesStore(s => s.checkUnread);
    const hydrateDrawerMenu = useDrawerMenuStore((state) => state.hydrate);

    useEffect(() => {
        hydrateDrawerMenu();
    }, []);

    useEffect(() => {
        (async () => {
            await initReleaseNotes();
            const latest = getLatestReleaseNote();
            if (latest) checkUnread(latest.date);
        })();
    }, [initReleaseNotes, checkUnread]);

    const currentSelectedLink = useMemo(() => {
        if (!isLandscape) return "";
        if (pathname.endsWith("/search") || pathname === "/search") return "Search";
        if (pathname.includes("assistant")) return "Assistant";
        if (pathname.includes("notifications")) return "Notifications";
        else if (pathname.includes("analytics") || pathname.includes("reports") || pathname.includes("dashboards")) return "Analytics";
        else if (pathname.includes("people")) return "People";
        else if (pathname.includes("events")) return "Calendar";
        else if (pathname.includes("connect")) return "Connect";
        else if (pathname.includes("follow-up")) return "FollowUp";
        else if (pathname.includes("automations") || pathname.includes("paths")) return "Automations";
        else if (pathname.includes("tasks")) return "Tasks";
        else if (pathname.includes("customfields")) return "CustomFields";
        else if (pathname.includes("library")) return "Library";
        else if (pathname.includes("files")) return "Files";
        else if (pathname.includes("roles")) return "Roles";
        else if (pathname.includes("biblestudies")) return "BibleStudies";
        else if (pathname.includes("smallgroups")) return "SmallGroups";
        else if (pathname.includes("settings/organization/admin")) return "AdminDashboard";
        else if (pathname.includes("training")) return "Training";
        else if (pathname.includes("blackoutperiods")) return "BlackoutPeriods";
        else if (pathname.includes("registrations")) return "Registrations";
        else if (pathname.replace(/\/$/, '').endsWith('/give')) return "Give";
        else if (pathname.includes("settings")) return "Settings";
        else if (pathname.includes("contributions")) return "Contributions";
        else if (pathname.includes("forms")) return "Forms";
        else if (pathname.includes("guides")) return "Help";
        else return "Home";
    }, [pathname, isLandscape]);

    // Close the slideout drawer before pushing so closeDrawer() schedules the
    // Reanimated animation before navigation work hits the JS thread.
    const navRoute = (href) => {
        if (!onClose) {
            router.push(href);
            return;
        }
        onClose();
        requestAnimationFrame(() => router.push(href));
    };

    const nav = (key, route) => {
        const isCurrent = isLandscape
            ? currentSelectedLink == key && pathname.endsWith(route)
            : route === "/" ? pathname === "/" : pathname.endsWith(route);
        if (isCurrent) {
            if (onClose) onClose();
            return;
        }
        navRoute(`/(tabs)/${currentTab}${route}`);
    };

    const goToOrgSelect = async () => {
        if (onClose) onClose();
        useEditScreenStore.getState().clearAllEditScreenData();
        await logOutOfOrganization();
        router.dismissAll();
        router.replace("/auth/select-organization");
    }

    const handleSignOut = () => {
        if (onClose) onClose();
        showAlert({ title: "Sign out your account?", cancelText: "Cancel", confirmText: "Log Out", onConfirm: async () => {
            await signOut('local', 'drawer-user-requested');
            router.replace("/auth/login");
        }});
    };

    const accountMenuOptions = [
        {
            name: "View Profile",
            icon: { android: "person", ios: "person.crop.circle", web: "person-circle-outline" },
            onSelect: () => nav("People", "/people/" + loggedInUser?.id),
        },
        {
            name: "Settings",
            icon: dropdownIcons.settings,
            onSelect: () => nav("Settings", "/settings"),
        },
        {
            name: "Switch Organization",
            icon: { android: "swap_horiz", ios: "arrow.left.arrow.right", web: "swap-horizontal-outline" },
            onSelect: goToOrgSelect,
        },
        { separator: true },
        {
            name: "Sign Out",
            icon: { android: "logout", ios: "rectangle.portrait.and.arrow.right", web: "log-out-outline" },
            destructive: true,
            onSelect: handleSignOut,
        },
    ];

    const handleAttachScreenshot = async () => {
        if (!active || !feedbackSheetOpen) return;
        const generation = ++feedbackGeneration.current;
        const isCurrent = () => feedbackGeneration.current === generation;
        try {
            const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
            if (!isCurrent()) return;
            if (!permission?.granted) {
                Toast.show({ type: 'error', text2: 'Media library permission is required to attach a screenshot.' });
                return;
            }

            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ['images'],
                allowsEditing: false,
                quality: 1,
            });

            if (!isCurrent() || result.canceled || !result.assets?.length) return;

            const asset = result.assets[0];
            const name = asset.fileName || `screenshot_${Date.now()}.jpg`;
            const contentType = mime.getType(name) || 'image/jpeg';

            let uri = asset.uri;
            if (!isWeb) {
                const saved = await renderManipulatedImage({uri: asset.uri, compress: 0.8, context: 'DrawerMenu.handleAttachScreenshot'});
                if (!isCurrent()) return;
                uri = saved?.uri || asset.uri;
            }

            let data;
            if (isWeb) {
                const response = await fetch(uri);
                if (!isCurrent()) return;
                const arrayBuffer = await response.arrayBuffer();
                if (!isCurrent()) return;
                data = new Uint8Array(arrayBuffer);
            } else {
                const file = new File(uri);
                const base64 = await file.base64();
                if (!isCurrent()) return;
                const arrayBuffer = decode(base64);
                data = new Uint8Array(arrayBuffer);
            }

            setFeedbackAttachment({ uri, name, contentType, data });
        } catch (error) {
            if (!isCurrent()) return;
            console.debug('Error attaching screenshot', error);
            Toast.show({ type: 'error', text2: 'Failed to attach screenshot. Please try again.' });
        }
    };

    return <>
        <Animated.View style={[styles.container(isWeb, asSlideout), collapsible && sidebarWidthStyle]}>
            <DrawerCollapseContext.Provider value={collapseCtx}>
            <View style={styles.innerContainer}>

                {/* ── HEADER ── */}
                {isPortrait ? (
                    <View style={styles.portraitHeader}>
                        <View style={styles.reachLogoRow}>
                            <Image
                                source={scheme === "light" ? require('../../assets/logo/GhostWhite.png') : require('../../assets/logo/GhostWhite.png')}
                                style={styles.reachLogoImg}
                                contentFit="contain"
                            />
                            <View style={styles.headerActions}>
                                <Pressable testID="drawer-menu-search-icon" style={styles.headerBtn} onPress={() => setSearchSheetOpen(true)}>
                                    <Unicon name="search-outline" size={22} color={rt.themeName === "dark" ? colors.text : colors.onPrimary} />
                                </Pressable>
                                {/* Notifications are a Paid module — hide the entry point on Free orgs. */}
                                {!isFree && (
                                    <Pressable testID="drawer-menu-notifications-icon" style={styles.headerBtn} onPress={() => nav("Notifications", "/notifications")}>
                                        <Unicon name="notifications-outline" size={22} color={rt.themeName === "dark" ? colors.text : colors.onPrimary} />
                                        <NotificationBadge count={notificationCount} size={16} style={styles.notifBadge} testID="drawer-menu-notifications-badge" />
                                    </Pressable>
                                )}
                            </View>
                        </View>
                        <View style={styles.portraitHeaderContent}>
                            <Avatar borderGap disableBorder size={56} profile={loggedInUser} editable />
                            <Pressable onPress={() => nav("People", "/people/" + loggedInUser?.id)} style={styles.portraitUserInfo}>
                                <View testID="drawer-menu-current-user" style={styles.portraitNameRow}>
                                    <Text style={styles.portraitName}>{loggedInUser?.firstName} {loggedInUser?.lastName}</Text>
                                    <Unicon name="chevron-forward-outline" size={16} style={{paddingTop:2, color:'white'}} />
                                </View>
                                <Text style={styles.portraitOrg}>{loggedInUser?.email}</Text>
                            </Pressable>
                        </View>
                        {organization?.logoPublicUrl && (
                            <View style={styles.portraitLogoBg(scheme)} pointerEvents="none">
                                <Image transition={250} source={scheme === "light" ? {uri: organization?.logoPublicUrlDark} : {uri: organization?.logoPublicUrl}} style={styles.portraitLogoImg} />
                            </View>
                        )}
                    </View>
                ) : <View style={styles.sidebarHeaderWeb} />
                }

                {active && loadingOrganization && Skeletons.drawerMenu()}

                {/* ── ORG SWITCHER (landscape only) ── */}
                {(!loadingOrganization) && (
                    <Pressable testID="drawer-menu-select-organization" accessibilityRole="button" accessibilityLabel={organization?.name ? `Switch organization: ${organization.name}` : "Switch organization"} onPress={goToOrgSelect} style={styles.orgBox}>
                        {organization?.logoPublicUrl ? (
                            <Image transition={250} source={scheme === "light" ? {uri: organization?.logoPublicUrlDark} : {uri: organization?.logoPublicUrl}} style={{width: 32, height: 32}} contentFit="contain" />
                        ) : (
                            <View style={styles.orgLogoPlaceholder}>
                                <Unicon name="business-outline" size={16} colorVariant="textTertiary" />
                            </View>
                        )}
                        {/* Fades out in the icon rail; stays mounted so the logo keeps its position. */}
                        <Animated.View style={[styles.orgTextWrap, fadeStyle]}>
                            <View style={styles.orgText}>
                                <Text style={styles.orgName} numberOfLines={1}>{organization?.name}</Text>
                                <Text style={styles.orgHandle} numberOfLines={1}>{organization?.handle}</Text>
                            </View>
                            <Unicon name="swap-horizontal-outline" size={14} colorVariant="textTertiary" />
                        </Animated.View>
                    </Pressable>
                )}

                {/* ── ASSISTANT (mobile only) ── */}
                {!loadingOrganization && !isWeb && loggedInUser?.can?.('Use AI Assistant') && (
                    <Pressable
                        testID="drawer-menu-assistant"
                        accessibilityRole="button"
                        onPress={() => nav("Assistant", "/assistant")}
                        style={styles.assistantButton}
                    >
                        <Unicon name="sparkles-outline" size={22} colorVariant="tertiary" />
                        <Animated.View style={[styles.assistantText, fadeStyle]}>
                            <Text style={styles.assistantLabel}>Assistant</Text>
                            <Text style={styles.assistantSublabel}>Ask about your church data</Text>
                        </Animated.View>
                    </Pressable>
                )}

                {/* ── NAV LINKS ── */}
                {!loadingOrganization && (
                    <ScrollView showsVerticalScrollIndicator={false} style={styles.scrollArea} contentContainerStyle={styles.scrollContent}keyboardShouldPersistTaps="handled"
>

                        <NavSection label="Main" testID="drawer-section-main" sectionKey="main">

                        {(!isFree || loggedInUser?.isAdmin || loggedInUser?.isAccountOwner) && (
                            <NavItem testID="drawer-menu-home" feature="dashboard" label="Dashboard" isSelected={currentSelectedLink == "Home"} onPress={() => nav("Home", "/")} />
                        )}

                        {(loggedInUser && loggedInUser?.can?.("View People")) && (
                            <NavItem testID="drawer-menu-people" feature="people" label="People Directory" isSelected={currentSelectedLink == "People"} onPress={() => nav("People", "/people")} />
                        )}

                        {/* Free: Give lives in Main for members (no Admin section).
                            enableGiving comes from the entitlements payload —
                            the same source that gates the merchant query. */}
                        {entitlements?.enableGiving === true && givingMerchant?.merchantStatus === 'ACTIVE' && (
                            <NavItem
                                testID="drawer-menu-give"
                                feature="give"
                                label="Give"
                                isSelected={currentSelectedLink == 'Give'}
                                onPress={() => nav('Give', '/give')}
                            />
                        )}

                        {!isFree && (
                            <>
                            <NavItem testID="drawer-menu-events" feature="events" label="Events" isSelected={currentSelectedLink == "Calendar"} onPress={() => nav("Calendar", "/events")} />
                            <NavItem testID="drawer-menu-tasks" feature="tasks" label="Tasks" isSelected={currentSelectedLink == "Tasks"} onPress={() => nav("Tasks", "/tasks")} />

                            {(loggedInUser && loggedInUser?.can?.("View Training")) && (
                                <NavItem testID="drawer-menu-training" feature="training" label="Training" isSelected={currentSelectedLink == "Training"} onPress={() => nav("Training", "/training")} />
                            )}

                            {(loggedInUser && loggedInUser?.can?.("View Paths")) && (
                                <NavItem testID="drawer-menu-automations" feature="paths" label="Automations" isSelected={currentSelectedLink == "Automations"} onPress={() => nav("Automations", "/automations")} />
                            )}

                            {(loggedInUser && loggedInUser?.can?.("View Follow Up")) && (
                                <NavItem testID="drawer-menu-follow-up" feature="followUp" icon="user-round-arrow-left" label="Follow Up" isSelected={currentSelectedLink == "FollowUp"} onPress={() => nav("FollowUp", "/follow-up")} />
                            )}

                            <NavItem testID="drawer-menu-forms" feature="forms" label="Forms" isSelected={currentSelectedLink == "Forms"} onPress={() => nav("Forms", "/forms")} />

                            {(loggedInUser && loggedInUser?.can?.("View Files")) && (
                                <NavItem testID="drawer-menu-files" feature="files" label="Files" isSelected={currentSelectedLink == "Files"} onPress={() => nav("Files", "/files")} />
                            )}

                            <NavItem
                                testID="drawer-menu-connect"
                                feature="connect"
                                label="Connect"
                                isSelected={currentSelectedLink == "Connect"}
                                onPress={() => nav("Connect", "/connect")}
                                badge={connectUnread}
                            />
                            </>
                        )}

                        {smallGroupTypes?.filter(type => (bibleStudiesEnabled ? type.purpose !== 'BIBLE_STUDY' : true) && (!type.roleID || loggedInUser?.Roles?.some(r => r.id === type.roleID) || loggedInUser?.isAdmin)).map(type => (
                            <NavItem
                                key={type.id}
                                testID={`drawer-menu-smallgroups-${type.id}`}
                                feature="groups"
                                icon={type.icon || 'people-outline'}
                                label={type.name}
                                isSelected={pathname.includes("smallgroups") && currentTypeID === type.id}
                                onPress={() => {
                                    if (pathname.includes("smallgroups") && currentTypeID === type.id) {
                                        if (onClose) onClose();
                                        return;
                                    }
                                    navRoute(`/(tabs)/${currentTab}/smallgroups?typeID=${type.id}`);
                                }}
                            />
                        ))}

                        {canViewBibleStudies && (
                            <NavItem
                                testID="drawer-menu-biblestudies"
                                feature="groups"
                                icon="book-outline"
                                label="Bible Studies"
                                isSelected={pathname.includes("biblestudies")}
                                onPress={() => navRoute(`/(tabs)/${currentTab}/biblestudies`)}
                            />
                        )}

                        </NavSection>

                        {/* Free members get no Admin section; Free admins get
                            the fixed subset (dashboard, giving, registrations). */}
                        {(!isFree || loggedInUser?.isAdmin) && (
                        <NavSection label="Admin" testID="drawer-section-admin" sectionKey="admin">

                        {loggedInUser?.isAdmin && (
                            <NavItem testID="drawer-menu-admin-dashboard" icon="shield-checkmark-outline" label="Admin Dashboard" isSelected={currentSelectedLink == "AdminDashboard"} onPress={() => nav("AdminDashboard", "/settings/organization/admin")} />
                        )}

                        {(loggedInUser && loggedInUser?.can?.("View Contributions")) && (
                            <NavItem testID="drawer-menu-contributions" feature="giving" label="Giving Management" isSelected={currentSelectedLink == "Contributions"} onPress={() => nav("Contributions", "/contributions")} />
                        )}

                        {!isFree && (loggedInUser?.canAny?.(["Create Roles", "Edit Roles", "Delete Roles"]) || loggedInUser?.ManagingRoles?.length > 0) && (
                            <NavItem testID="drawer-menu-roles" feature="roles" label={loggedInUser?.isAdmin ? "Roles & Permissions" : "Roles"} isSelected={currentSelectedLink == "Roles"} onPress={() => nav("Roles", "/roles")} />
                        )}

                        {loggedInUser?.isAdmin && (
                            <NavItem testID="drawer-menu-registrations" icon="person-add-outline" label="Registration Requests" isSelected={currentSelectedLink == "Registrations"} onPress={() => nav("Registrations", "/registrations")} badge={registrationCount} />
                        )}

                        {!isFree && (
                            <NavItem testID="drawer-menu-analytics" feature="analytics" label="Reports & Analytics" isSelected={currentSelectedLink == "Analytics"} onPress={() => nav("Analytics", "/analytics")} />
                        )}

                        </NavSection>
                        )}

                        <NavSection label="Account" testID="drawer-section-account" sectionKey="account">

                        {(!selectedTabKeys.includes('settings') || isLandscape) && (
                            <NavItem testID="drawer-menu-settings" icon="settings-outline" label="Settings" isSelected={currentSelectedLink == "Settings"} onPress={() => nav("Settings", "/settings")} />
                        )}

                        {isPortrait && (
                            <>
                                <NavItem testID="drawer-menu-feedback" icon="chatbubble-ellipses-outline" label="Send Feedback" isSelected={false} onPress={() => setFeedbackSheetOpen(true)} />
                                <NavItem testID="drawer-menu-features" icon="hammer-outline" label="Features & Fixes" isSelected={false} badge={hasUnread ? 1 : 0} onPress={() => {
                                    setReleaseNotesOpen(true);
                                    const latest = getLatestReleaseNote();
                                    if (latest) markAsSeen(latest.date);
                                }} />
                            </>
                        )}

                        <NavItem testID="drawer-menu-help" icon="help-circle-outline" label="Help" isSelected={pathname.includes('/guides')} onPress={() => navRoute(`/(tabs)/${currentTab}/guides`)} />

                        {isPortrait && (
                            <NavItem testID="drawer-menu-sign-out" icon="log-out-outline" label="Sign Out" destructive isSelected={false} onPress={handleSignOut} />
                        )}
                        </NavSection>

                    </ScrollView>
                )}

                {/* ── FOOTER (landscape only) ── */}
                {(isLandscape && !loadingOrganization) && (
                    <View style={styles.footer}>
                        <View style={styles.footerUtilities(hideDetails)}>
                            <Pressable testID="drawer-menu-feedback" accessibilityRole="button" accessibilityLabel="Send feedback" style={styles.footerUtility} onPress={() => setFeedbackSheetOpen(true)}>
                                <Unicon name="chatbubble-ellipses-outline" size={15} colorVariant="textSecondary" />
                                {!hideDetails && <Animated.View style={fadeStyle}><Text style={styles.footerUtilityText}>Feedback</Text></Animated.View>}
                            </Pressable>
                            <Pressable testID="drawer-menu-features" accessibilityRole="button" accessibilityLabel="What’s new" style={styles.footerUtility} onPress={() => {
                                setReleaseNotesOpen(true);
                                const latest = getLatestReleaseNote();
                                if (latest) markAsSeen(latest.date);
                            }}>
                                <View style={styles.footerIconWrap}>
                                    <Unicon name="hammer-outline" size={15} colorVariant="textSecondary" />
                                    {hasUnread && <View style={styles.badge}><Badge size={12}>1</Badge></View>}
                                </View>
                                {!hideDetails && <Animated.View style={fadeStyle}><Text style={styles.footerUtilityText}>What’s New</Text></Animated.View>}
                            </Pressable>
                        </View>
                        <View style={styles.footerUserCard(collapsed)}>
                            <DropdownMenu
                                testID="DrawerUserMenu"
                                title="Account"
                                align="end"
                                options={accountMenuOptions}
                                containerStyle={!collapsed && styles.footerUserMenuWrap}
                                triggerComponent={(
                                    <View testID="drawer-menu-user" style={styles.footerUser(collapsed)}>
                                        <Avatar borderGap disableBorder size={38} profile={loggedInUser} />
                                        {!hideDetails && (
                                            <Animated.View style={[styles.footerUserText, fadeStyle]}>
                                                <Text style={styles.footerUserName} numberOfLines={1}>{loggedInUser?.firstName} {loggedInUser?.lastName}</Text>
                                                <Text style={styles.footerUserEmail} numberOfLines={1}>{loggedInUser?.email}</Text>
                                            </Animated.View>
                                        )}
                                        {!hideDetails && (
                                            <Animated.View style={fadeStyle}>
                                                <View style={styles.footerMenuIcon}>
                                                    <Unicon name="ellipsis-horizontal" size={16} colorVariant="textSecondary" />
                                                </View>
                                            </Animated.View>
                                        )}
                                    </View>
                                )}
                            />
                            {collapsible && (
                                <Pressable
                                    testID="drawer-menu-collapse-toggle"
                                    accessibilityRole="button"
                                    accessibilityLabel={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                                    onPress={toggleCollapsed}
                                    style={styles.collapseToggleBtn}
                                    hitSlop={6}
                                >
                                    <Unicon name={collapsed ? "chevron-forward-outline" : "chevron-back-outline"} size={16} colorVariant="textTertiary" />
                                </Pressable>
                            )}
                        </View>
                    </View>
                )}

            </View>
            </DrawerCollapseContext.Provider>
        </Animated.View>

        <SheetModal title="Search" isOpen={searchSheetOpen} onCancel={() => setSearchSheetOpen(false)} unmountOnClose>
            <SearchModal onSelect={(_item) => setSearchSheetOpen(false)} />
        </SheetModal>

        <SheetModal
            title="Send Feedback"
            isOpen={feedbackSheetOpen}
            onCancel={resetFeedback}
            unmountOnClose
            snapPoints={[500, 800]}
            confirmText="Send"
            onConfirm={() => {
                if (!feedbackMessage || !feedbackType) {
                    Toast.show({ type: "error", text2: "Please enter some feedback!" });
                    return;
                }
                let sentryEventID = lastSentryEventID;
                if (!sentryEventID) {
                    sentryEventID = Sentry.captureMessage("User Feedback - No direct error", "info");
                }
                const attachment = feedbackAttachment
                    ? { filename: feedbackAttachment.name, data: feedbackAttachment.data, contentType: feedbackAttachment.contentType }
                    : null;
                Sentry.captureFeedback(
                    {
                        associatedEventId: sentryEventID,
                        name: loggedInUser?.fullName,
                        email: loggedInUser?.email,
                        message: feedbackType + ": " + feedbackMessage,
                    },
                    attachment ? { attachments: [attachment] } : undefined
                );
                resetFeedback();
                Toast.show({ type: "success", text2: "Thank you for your feedback! We'll reach out if more details are needed." });
            }}
        >
            <Callout type="info" message="Please provide as much information as possible about your issue or feature request. We may reach out with additional questions if needed." />
            <ToggleInput title="Feedback Type" items={['Error/Crash', 'Bug Report', 'Feature Request', 'Other']} selectedValues={feedbackType} onItemChanged={(value) => setFeedbackType(value)} itemsPerRow={isLandscape ? 4 : 2} />
            <Input isNotOnSurface title="Message" multiline={true} value={feedbackMessage} onUpdate={(new_value) => setFeedbackMessage(new_value)} />
            {!feedbackAttachment && (
                <Pressable testID="drawer-menu-attach-screenshot" onPress={handleAttachScreenshot} style={styles.attachmentButton}>
                    <Unicon name="cloud-upload-outline" size={28} colorVariant="primary" />
                    <Text style={styles.attachmentButtonText}>Upload File</Text>
                </Pressable>
            )}
            {feedbackAttachment && (
                <View style={styles.attachmentPreview}>
                    <RNImage source={{ uri: feedbackAttachment.uri }} style={styles.attachmentImage} resizeMode="contain" />
                    <Pressable testID="drawer-menu-remove-screenshot" onPress={() => {
                        feedbackGeneration.current += 1;
                        setFeedbackAttachment(null);
                    }} style={styles.removeAttachment}>
                        <Unicon name="close" size={14} colorVariant="onPrimary" />
                    </Pressable>
                </View>
            )}
        </SheetModal>

        <ReleaseNotesModal isOpen={releaseNotesOpen} onClose={() => setReleaseNotesOpen(false)} />
    </>;
}, (previous, next) =>
    previous.active === next.active &&
    previous.asSlideout === next.asSlideout &&
    previous.onClose === next.onClose &&
    (!next.active || previous.menuState === next.menuState)
);

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, roundness }, rt) => ({
    container: (isWeb, asSlideout) => ({
        zIndex: 1000,
        variants: {
            isLandscape: {
                true: { width: 260, height: rt.screen.height - (isWeb ? 64 : 84), borderRightColor: colors.borderSubtle, borderRightWidth: 1 },
                false: asSlideout ? { flex: 1, width: rt.screen.width - 80 } : { position: "absolute", width: rt.screen.width, height: rt.screen.height + 60 },
            },
        },
    }),
    innerContainer: {
        backgroundColor: colors.surface,
        flex: 1,
        overflow: "hidden", // clip text while the width animation runs
        variants: {
            isLandscape: {
                true: { maxWidth: 260 },
                false: { maxWidth: 420 },
            },
        },
    },

    /* ── Portrait Header ── */
    portraitHeader: {
        backgroundColor: rt.themeName == "dark" ? colors.onPrimary : colors.primary,
        paddingTop: 60,
        paddingBottom: spacing.lg,
        paddingHorizontal: spacing.lg,
        overflow: "hidden",
    },
    reachLogoRow: {
        marginBottom: spacing.md,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },
    reachLogoImg: {
        height: 22,
        width: 80,
        opacity: 0.85,
    },
    portraitHeaderContent: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
    },
    portraitUserInfo: {
        flex: 1,
    },
    portraitNameRow: {
        flexDirection: "row",
        alignItems: "center",
    },
    portraitName: {
        fontFamily: fonts.bold,
        color: 'white', //rt.themeName == "dark" ? colors.text : colors.onPrimary,
        fontSize: typography.h2,
    },
    portraitOrg: {
        color: rt.themeName == "dark" ? colors.text : colors.onPrimary,
        opacity: 0.7,
        fontSize: typography.caption,
        marginTop: spacing.xs,
    },
    portraitLogoBg: (scheme) => ({
        position: "absolute",
        top: 10,
        right: -40,
        width: 200,
        height: 200,
        opacity: scheme === "dark" ? 0.06 : 0.15,
    }),
    portraitLogoImg: {
        width: 200,
        height: 200,
    },

    /* ── Sidebar Header ── */
    sidebarHeaderWeb: {
        height: spacing.sm,
    },
    collapseToggleBtn: {
        width: 28,
        height: 28,
        borderRadius: 8,
        alignItems: "center",
        justifyContent: "center",
        _web: { _hover: { backgroundColor: colors.hover } },
    },
    sidebarHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: spacing.md,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
        variants: {
            isLandscape: {
                true: { height: 52 },
                false: { height: 92, paddingTop: 40 },
            },
        },
    },
    headerActions: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
    },
    headerBtn: {
        width: 32,
        height: 32,
        borderRadius: 8,
        alignItems: "center",
        justifyContent: "center",
        _web: { _hover: { backgroundColor: colors.hover } },
    },
    notifBadge: {
        position: "absolute",
        top: -4,
        right: -4,
    },

    /* ── Org Switcher ── */
    orgBox: {
        flexDirection: "row",
        alignItems: "center",
        margin: spacing.sm,
        padding: spacing.sm,
        borderRadius: roundness.md,
        backgroundColor: colors.surfaceVariant,
        gap: spacing.sm,
        _web: { _hover: { backgroundColor: colors.hoverStrong } },
    },
    orgLogoPlaceholder: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.border,
        alignItems: "center",
        justifyContent: "center",
    },
    orgTextWrap: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
    },
    orgText: {
        flex: 1,
    },
    orgName: {
        fontFamily: fonts.heading,
        fontSize: typography.caption,
        color: colors.text,
    },
    orgHandle: {
        fontSize: typography.micro,
        color: colors.textTertiary,
        marginTop: 1,
    },
    assistantButton: {
        flexDirection: "row",
        alignItems: "center",
        marginHorizontal: spacing.sm,
        marginBottom: spacing.sm,
        padding: spacing.sm,
        gap: spacing.sm,
    },
    assistantText: {
        flex: 1,
    },
    assistantLabel: {
        fontFamily: fonts.heading,
        fontSize: typography.body,
        color: colors.tertiary,
    },
    assistantSublabel: {
        fontSize: typography.micro,
        color: colors.tertiary,
        opacity: 0.7,
        marginTop: 1,
    },

    /* ── Nav Items ── */
    scrollArea: {
        flex: 1,
        paddingHorizontal: spacing.sm,
    },
    scrollContent: {
        paddingTop: spacing.xs,
        paddingBottom: 150,
    },
    sectionHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: spacing.md,
        marginBottom: spacing.xs,
        marginLeft: spacing.sm,
        paddingRight: spacing.sm,
        paddingVertical: 2,
        _web: {
            transition: "all 0.12s ease",
            _hover: { opacity: 0.7 },
        },
    },
    sectionLabel: {
        fontSize: typography.micro,
        fontFamily: fonts.bold,
        color: colors.textTertiary,
        textTransform: "uppercase",
        letterSpacing: 0.8,
    },
    navItem: (isSelected, collapsed) => ({
        position: "relative",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        paddingVertical: 7,
        paddingHorizontal: spacing.sm,
        borderRadius: roundness.sm,
        marginVertical: 1,
        backgroundColor: isSelected ? colors.hoverStrong : "transparent",
        overflow: "hidden",
        _web: {
            transition: "background-color 0.12s ease, transform 0.12s ease",
            _hover: {
                backgroundColor: colors.hoverStrong,
                transform: [{ translateX: 2 }],
            },
        },
    }),
    selectedIndicator: {
        position: "absolute",
        left: 0,
        top: 7,
        bottom: 7,
        width: 3,
        borderRadius: 2,
        backgroundColor: colors.primary,
    },
    navIconWrap: (collapsed) => ({
        width: 32,
        height: 32,
        alignItems: "center",
        justifyContent: "center",
        marginRight: collapsed ? 0 : spacing.sm,
    }),
    navLabelWrap: {
        flex: 1,
        justifyContent: "center",
        overflow: "hidden",
    },
    navLabel: (isSelected, destructive) => ({
        fontFamily: isSelected ? fonts.heading : fonts.body,
        color: destructive ? colors.error : isSelected ? colors.primary : colors.text,
        fontSize: typography.body,
        includeFontPadding: false,
        textAlignVertical: "center",
    }),
    navGroupHeader: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 7,
        paddingHorizontal: spacing.sm,
        borderRadius: 8,
        marginVertical: 1,
    },
    navGroupLabel: (isSelected) => ({
        flex: 1,
        fontFamily: isSelected ? fonts.heading : fonts.body,
        color: isSelected ? colors.primary : colors.text,
        fontSize: typography.body,
    }),
    navSubItem: (isSelected) => ({
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 7,
        paddingLeft: 28 + spacing.sm * 3,
        paddingRight: spacing.sm,
        borderRadius: 8,
        marginVertical: 1,
        backgroundColor: isSelected ? colors.hoverStrong : "transparent",
        _web: {
            transition: "all 0.12s ease",
            _hover: { backgroundColor: isSelected ? colors.hoverStrong : colors.hover },
        },
    }),
    navSubLabel: (isSelected) => ({
        flex: 1,
        fontFamily: isSelected ? fonts.heading : fonts.body,
        color: isSelected ? colors.primary : colors.text,
        fontSize: typography.body - 1,
    }),
    badge: {
        position: "absolute",
        top: -2,
        right: -2,
    },

    /* ── Footer ── */
    footer: {
        borderTopWidth: 1,
        borderTopColor: colors.borderSubtle,
        padding: spacing.sm,
        gap: spacing.xs,
        backgroundColor: colors.surface,
    },
    footerUtilities: (collapsed) => ({
        flexDirection: collapsed ? "column" : "row",
        gap: spacing.xs,
    }),
    footerUtility: {
        flex: 1,
        minHeight: 34,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.xs,
        borderRadius: roundness.sm,
        _web: { _hover: { backgroundColor: colors.hover } },
    },
    footerUtilityText: {
        fontSize: typography.micro + 1,
        color: colors.textSecondary,
        fontFamily: fonts.body,
    },
    footerIconWrap: {
        position: 'relative',
    },
    footerUserCard: (collapsed) => ({
        minHeight: 58,
        flexDirection: collapsed ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        padding: collapsed ? spacing.xs : spacing.sm,
        gap: spacing.sm,
        borderRadius: roundness.md,
        backgroundColor: colors.surfaceVariant,
        _web: {
            transition: "background-color 0.12s ease",
            _hover: { backgroundColor: colors.hoverStrong },
        },
    }),
    footerUserMenuWrap: {
        flex: 1,
        minWidth: 0,
    },
    footerUser: (collapsed) => ({
        flexDirection: "row",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: spacing.sm,
        alignSelf: "stretch",
    }),
    footerUserText: {
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
    },
    footerUserName: {
        fontFamily: fonts.heading,
        fontSize: typography.body,
        color: colors.text,
    },
    footerUserEmail: {
        fontSize: typography.micro + 1,
        color: colors.textTertiary,
        marginTop: 1,
    },
    footerMenuIcon: {
        width: 28,
        height: 28,
        borderRadius: 14,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.surface,
    },
    attachmentButton: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: spacing.md,
        padding: spacing.lg,
        borderRadius: roundness.md,
        borderWidth: 2,
        borderStyle: 'dashed',
        borderColor: colors.primary,
        backgroundColor: colors.surfaceVariant,
        _web: { _hover: { backgroundColor: colors.hoverStrong } },
    },
    attachmentButtonText: {
        fontFamily: fonts.body,
        fontSize: typography.body,
        color: colors.text,
        marginTop: spacing.sm,
    },
    attachmentPreview: {
        marginTop: spacing.md,
        width: 150,
        height: 150,
        borderRadius: roundness.md,
        overflow: 'hidden',
        backgroundColor: colors.surfaceVariant,
    },
    attachmentImage: {
        width: '100%',
        height: '100%',
    },
    removeAttachment: {
        position: 'absolute',
        top: spacing.xs,
        right: spacing.xs,
        width: 24,
        height: 24,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.error,
    },
}));

export default DrawerMenu;